import React, { useState, useRef, useEffect } from "react";
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useHistory } from "react-router-dom";
import {
  getCsvFile,
  getPatientVisitDetails,
  updateVisitData,
} from "../../actions/patientActions";
import { getEegGraph, getEegGraphFilter } from "../../actions/graphActions";
import fastBackwardIcon from "../../assets/img/svg/fast-backward-icon.svg";
import backwardIcon from "../../assets/img/svg/ten_second_back.svg";
import playIcon from "../../assets/img/svg/play-icon.svg";
import pauseIcon from "../../assets/img/svg/pause-icon.svg";
import fastForwardIcon from "../../assets/img/svg/fast-forward-icon.svg";
import forwardIcon from "../../assets/img/svg/ten_second_front.svg";
import fiveSecondForward from "../../assets/img/svg/five_second_front.svg";
import fiveSecondBackward from "../../assets/img/svg/five_second_back.svg";
import BackIcon from "../../assets/img/svg/back-icon.svg";
import zoomInIcon from "../../assets/img/svg/zoom-in-icon.svg";
import zoomOutIcon from "../../assets/img/svg/zoom-out-icon.svg";
import enhanceIcon from "../../assets/img/enhance_icon.svg";
import resetFilterIcon from "../../assets/img/resetFilter.svg";
import resetZoomIcon from "../../assets/img/svg/reset-zoom-icon.svg";
import { ArrowForwardIos, ArrowBackIos } from "@mui/icons-material";
import { formatName, formatDate } from "../../helper";
import HeatMap from "./HeatMap";
import LinearWithValueLabel from "../loader/LinearLoaderLablel";
import "./recording2.css";
import Tooltip from "@mui/material/Tooltip";
import NotesEditor from "./NotesEditor";
import UpdateLocationTagConfirmModal from "./UpdateLocationTagConfirmModal";

const conditionMap = {
  1: "seizure",
  2: "concussion",
  3: "stroke",
  4: "Normal",
  5: "Altered Mental Status",
  0: "0",
};
const genderMap = {
  1: "M",
  2: "F",
};

Chart.register(zoomPlugin);

const Recording = (props) => {
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [editing, setEditing] = useState(false);
  const [lffValue, setLffValue] = useState("Select");
  const [hffValue, setHffValue] = useState("Select");
  const [notchValue, setNotchValue] = useState("Select");

  const [charts, setCharts] = useState([]);
  const chartRefs = useRef([]);
  const [dataCount, setDataCount] = useState(0);
  const [dataLength, setDataLength] = useState(null);

  const [eegData, setEegData] = useState(null);
  const [patientData, setPatientData] = useState(null);

  const [filteredEegData, setFilteredEegData] = useState(null);
  const [isFilter, setIsFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const token = useSelector((state) => state.loginData.token);
  const [relatedIds, setRelatedIds] = useState(null);
  const { eegGraphData } = useSelector((state) => state.eegGraph);
  const { visitDetails } = useSelector((state) => state.patientVisitDetails);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [downloading, setDownloading] = useState(false);
  const [isFirmwareAdmin, setIsFirmwareAdmin] = useState(false);
  const [isResearchAdmin, setIsResearchAdmin] = useState(false);

  const [zoomState, setZoomState] = useState([
    { yMin: undefined, yMax: undefined },
    { yMin: undefined, yMax: undefined },
  ]);

  // Track current pan position for proper navigation
  const [currentPanWindow, setCurrentPanWindow] = useState({ min: 0, max: 10 });

  const [scrollbarPosition, setScrollbarPosition] = useState(0);
  const [totalDataDuration, setTotalDataDuration] = useState(0);

  // Trimming removed per latest requirements

  // Fix scrollbar handler to properly update chart bounds
  const handleScrollbarChange = (e) => {
    const newPosition = parseInt(e.target.value); // Cast to integer
    const totalDuration = getTotalDataDuration();

    // Clamp to valid range
    const minAllowedPosition = Math.min(WINDOW_SIZE_SECONDS, totalDuration);
    const clampedPosition = Math.max(newPosition, minAllowedPosition);
    const finalPosition = Math.min(clampedPosition, totalDuration);

    setScrollbarPosition(finalPosition);

    // Calculate window bounds - slider value is the window END
    const windowEnd = finalPosition;
    const windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);

    // Update current pan window state (source of truth)
    setCurrentPanWindow({ min: windowStart, max: windowEnd });

    // Derive dataCount from window position
    const newDataCount = Math.floor(windowStart / WINDOW_SIZE_SECONDS);
    setDataCount(newDataCount);

    // Directly update both charts' x-axis bounds with integer bounds
    chartRefs.current.forEach((canvasRef) => {
      if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
        let xMin = Math.floor(windowStart);
        let xMax = Math.ceil(windowEnd);

        // Ensure at least 1 second span to prevent x-axis disappearing
        if (xMax - xMin < 1) {
          xMax = xMin + 1;
        }

        canvasRef.chart.options.scales.x.min = xMin;
        canvasRef.chart.options.scales.x.max = xMax;
        // Ensure tick settings are preserved
        canvasRef.chart.options.scales.x.ticks.stepSize = 1;
        canvasRef.chart.options.scales.x.ticks.autoSkip = false;
        canvasRef.chart.update("none");
      }
    });
  };

  // Sync slider position with currentPanWindow (source of truth)
  useEffect(() => {
    // Slider shows the window end position
    const newSliderPosition = currentPanWindow.max;
    if (Math.abs(newSliderPosition - scrollbarPosition) > 0.1) {
      setScrollbarPosition(newSliderPosition);
    }
  }, [currentPanWindow]);

  const dummyLocationTags = ["Eye Open & Close", "Alpha", "SSVEP"];
  const [selectedLocationTag, setSelectedLocationTag] = useState("");
  const [showLocationTagConfirm, setShowLocationTagConfirm] = useState(false);
  const [pendingLocationTag, setPendingLocationTag] = useState("");

  // Constants for EEG visualization
  const WINDOW_SIZE_SECONDS = 10; // Default viewing window size

  // Concise console helper for EEG data summary
  const SAMPLE_RATE_HZ = 250;
  const logEEGDataSummary = (label, dataObj) => {
    if (!dataObj) {
      console.warn(`[EEG] ${label}: No data`);
      return;
    }
    const channels = ["CH1_Voltage", "CH2_Voltage"];
    console.groupCollapsed(
      `[EEG] ${label} summary (time = totalSamples/${SAMPLE_RATE_HZ})`
    );
    let combinedSamples = 0;
    channels.forEach((ch) => {
      const chunks = dataObj[ch] || [];
      const samplesPerChunk = chunks.map((c) =>
        Array.isArray(c) ? c.length : 0
      );
      const totalSamples = samplesPerChunk.reduce((a, b) => a + b, 0);
      const lastChunkSamples = samplesPerChunk.length
        ? samplesPerChunk[samplesPerChunk.length - 1]
        : 0;
      const timeSec = totalSamples / SAMPLE_RATE_HZ;
      combinedSamples += totalSamples;
      console.log(`${ch}`);
      console.log(`  chunks: ${chunks.length}`);
      console.log(`  samplesPerChunk:`, samplesPerChunk);
      console.log(`  lastChunkSamples: ${lastChunkSamples}`);
      console.log(`  totalSamples: ${totalSamples}`);
      console.log(
        `  timeSec: ${timeSec.toFixed(3)} (totalSamples/${SAMPLE_RATE_HZ})`
      );
    });
    console.log(`combinedTotalSamples: ${combinedSamples}`);
    console.log(
      `combinedTimeSec: ${(combinedSamples / SAMPLE_RATE_HZ).toFixed(
        3
      )} (combinedTotalSamples/${SAMPLE_RATE_HZ})`
    );
    console.groupEnd();
  };

  const logWindow = (source, win) => {
    if (!win) return;
    console.log(
      `[EEG] Window @ ${source}: start=${Math.round(
        win.min
      )}s, end=${Math.round(win.max)}s, span=${Math.round(win.max - win.min)}s`
    );
  };

  const handleBack = () => {
    window.history.back();
  };

  // Helper function to calculate time window
  const getTimeWindow = (dataCount, windowSize = WINDOW_SIZE_SECONDS) => {
    const startTime = dataCount * windowSize;
    const endTime = startTime + windowSize;
    return { startTime, endTime };
  };

  // Helper function to get total data duration
  const getTotalDataDuration = () => {
    if (!eegData?.CH1_Voltage || eegData.CH1_Voltage.length === 0) return 0;

    // Calculate total duration from actual samples, not chunk count
    const totalSamples = eegData.CH1_Voltage.reduce(
      (sum, chunk) => sum + chunk.length,
      0
    );
    const originalDuration = totalSamples / 250; // 250 samples per second

    // Round up to next whole second to prevent x-axis disappearing
    return Math.ceil(originalDuration);
  };

  // Check if we can navigate forward/backward considering both segments and pan position
  const canNavigateBackward = () => {
    return currentPanWindow.min > 0;
  };

  const canNavigateForward = () => {
    const totalDuration = getTotalDataDuration();
    return currentPanWindow.max < totalDuration;
  };

  // Flatten EEG data for panning functionality
  // This function converts segmented EEG data into continuous arrays for smooth horizontal panning
  const flattenEEGData = (channelData, timeData) => {
    const flatData = [];
    const flatLabels = [];

    if (!channelData || !timeData) return { flatData, flatLabels };

    // First, flatten all data as before
    const allData = [];
    const allLabels = [];

    channelData.forEach((chunk, index) => {
      if (!chunk || !timeData[index]) return;

      const baseTime = parseFloat(timeData[index][0] || "0.0"); // e.g., "0.0", "10.0"
      const step = 1 / 250; // Proper sample-based approach: 250 samples per second

      chunk.forEach((val, i) => {
        allData.push(val);
        allLabels.push(baseTime + i * step);
      });
    });

    // No trimming, use original data
    flatData.push(...allData);
    flatLabels.push(...allLabels);

    return { flatData, flatLabels };
  };

  // Update chart data when panning - synchronized for both charts
  const updateGraphOnPan = (chart, minX, maxX, labels, data, channelLabel) => {
    try {
      if (!chart || !labels || !data || labels.length === 0) return;
      if (!chart.data || !chart.data.datasets || !chart.data.datasets[0])
        return;

      // Instead of changing data, just update the scale bounds
      // This preserves the x-axis tick configuration
      if (chart.options && chart.options.scales && chart.options.scales.x) {
        // Force integer bounds to prevent x-axis disappearing
        let xMin = Math.floor(minX);
        let xMax = Math.ceil(maxX);

        // Ensure at least 1 second span to prevent x-axis disappearing
        if (xMax - xMin < 1) {
          xMax = xMin + 1;
        }

        chart.options.scales.x.min = xMin;
        chart.options.scales.x.max = xMax;

        // Ensure proper x-axis configuration
        chart.options.scales.x.type = "linear";
        chart.options.scales.x.ticks.display = channelLabel === "CH2_Voltage";
        chart.options.scales.x.ticks.stepSize = 1;
        chart.options.scales.x.ticks.maxTicksLimit = 11;
        chart.options.scales.x.title.display = channelLabel === "CH2_Voltage";
        chart.options.scales.x.title.text = "Time (seconds)";

        // Force the chart to update with new bounds
        chart.update("none");
      }
    } catch (error) {}
  };

  // Synchronized panning for both charts
  const updateBothChartsOnPan = (sourceChart, minX, maxX) => {
    try {
      // Update the current pan window state
      setCurrentPanWindow({ min: minX, max: maxX });
      logWindow("pan", { min: minX, max: maxX });

      chartRefs.current.forEach((canvasRef, index) => {
        if (
          canvasRef?.chart &&
          canvasRef?.fullDataRef &&
          canvasRef.chart.options
        ) {
          // Get channel label from stored reference
          const channelLabel =
            canvasRef.channelLabel ||
            (index === 0 ? "CH1_Voltage" : "CH2_Voltage");

          // Just update the x-axis bounds - no data changes needed
          updateGraphOnPan(
            canvasRef.chart,
            minX,
            maxX,
            canvasRef.fullDataRef.flatLabels,
            canvasRef.fullDataRef.flatData,
            channelLabel
          );
        }
      });
    } catch (error) {}
  };

  // Update the updateGraph function to force consistent ticks
  const updateGraph = (dataCount) => {
    const location = (
      visitDetails?.data?.[currentIndex]?.location || "N/A"
    ).split("-");

    const newCharts = ["CH1_Voltage", "CH2_Voltage"].map((channelName, i) => {
      const canvasRef = chartRefs.current[i];

      // If chart exists and has panning data, try to update existing chart
      if (canvasRef?.chart && canvasRef?.fullDataRef) {
        const { startTime, endTime } = getTimeWindow(dataCount);
        const totalDuration = getTotalDataDuration();

        // Calculate proper window bounds handling partial last window
        let windowStart = startTime;
        let windowEnd = endTime;

        // Clamp to actual data boundaries
        if (windowEnd > totalDuration) {
          windowEnd = totalDuration;
          windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);
        }

        // Update the current pan window state (source of truth)
        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        logWindow("updateGraph", {
          min: windowStart,
          max: windowEnd,
        });

        // Update data for the new time window with x-axis stability
        updateGraphOnPan(
          canvasRef.chart,
          windowStart,
          windowEnd,
          canvasRef.fullDataRef.flatLabels,
          canvasRef.fullDataRef.flatData,
          channelName
        );

        return canvasRef.chart;
      } else {
        // Create new chart if it doesn't exist
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
        return createGraph(
          canvasRef,
          eegGraphData?.data,
          channelName,
          location[i],
          dataCount,
          zoomState[i]
        );
      }
    });
    setCharts(newCharts);
  };

  const handleGraphButton = (type) => {
    switch (type) {
      case "fast-backward": {
        // Jump to the beginning
        const totalDuration = getTotalDataDuration();
        const windowStart = 0;
        const windowEnd = Math.min(WINDOW_SIZE_SECONDS, totalDuration);

        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(0);

        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "backward": {
        // Move window 10 seconds back, but not before 0
        const totalDuration = getTotalDataDuration();
        let windowStart = Math.max(0, currentPanWindow.min - 10);
        let windowEnd = Math.min(
          windowStart + WINDOW_SIZE_SECONDS,
          totalDuration
        );

        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));

        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "five-backward": {
        // Move window 5 seconds back, but not before 0
        const totalDuration = getTotalDataDuration();
        let windowStart = Math.max(0, currentPanWindow.min - 5);
        let windowEnd = Math.min(
          windowStart + WINDOW_SIZE_SECONDS,
          totalDuration
        );

        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));

        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "forward": {
        // Move window 10 seconds forward, handling partial last window
        const totalDuration = getTotalDataDuration();
        let windowStart = currentPanWindow.min + 10;
        let windowEnd = Math.min(
          windowStart + WINDOW_SIZE_SECONDS,
          totalDuration
        );

        // Ensure we don't exceed the data boundaries
        if (windowEnd > totalDuration) {
          windowEnd = totalDuration;
          windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);
        }

        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));

        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "five-forward": {
        // Move window 5 seconds forward, handling partial last window
        const totalDuration = getTotalDataDuration();
        let windowStart = currentPanWindow.min + 5;
        let windowEnd = Math.min(
          windowStart + WINDOW_SIZE_SECONDS,
          totalDuration
        );

        // Ensure we don't exceed the data boundaries
        if (windowEnd > totalDuration) {
          windowEnd = totalDuration;
          windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);
        }

        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));

        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "fast-forward": {
        // Jump to the last window showing data up to the end (may be partial)
        const totalDuration = getTotalDataDuration();
        const windowEnd = totalDuration;
        const windowStart = Math.max(0, windowEnd - WINDOW_SIZE_SECONDS);

        setCurrentPanWindow({ min: windowStart, max: windowEnd });
        setDataCount(Math.floor(windowStart / WINDOW_SIZE_SECONDS));

        // Update charts directly with integer bounds
        chartRefs.current.forEach((canvasRef) => {
          if (canvasRef?.chart && canvasRef.chart.options?.scales?.x) {
            let xMin = Math.floor(windowStart);
            let xMax = Math.ceil(windowEnd);

            // Ensure at least 1 second span to prevent x-axis disappearing
            if (xMax - xMin < 1) {
              xMax = xMin + 1;
            }

            canvasRef.chart.options.scales.x.min = xMin;
            canvasRef.chart.options.scales.x.max = xMax;
            canvasRef.chart.update("none");
          }
        });
        stopPlaying();
        return;
      }
      case "play":
        if (!isPlaying) {
          const totalDuration = getTotalDataDuration();
          // If at or past the end, reset to beginning
          if (currentPanWindow.max >= totalDuration) {
            setCurrentPanWindow({
              min: 0,
              max: Math.min(WINDOW_SIZE_SECONDS, totalDuration),
            });
            setDataCount(0);
          }
          startPlaying();
        }
        return;
      case "pause":
        stopPlaying();
        return;
      default:
        return;
    }
  };

  const startPlaying = () => {
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setDataCount((prevCount) => {
        const totalDuration = getTotalDataDuration();
        const nextCount = prevCount + 1;
        const nextWindowStart = nextCount * WINDOW_SIZE_SECONDS;

        // Check if we've reached the end using ceil duration
        if (nextWindowStart >= totalDuration) {
          stopPlaying();
          return prevCount;
        }

        return nextCount;
      });
    }, 500);
  };

  const stopPlaying = () => {
    setIsPlaying(false);
    clearInterval(intervalRef.current);
  };

  useEffect(() => {
    if (visitDetails?.data) {
      if (visitDetails?.data[0]?.patient_status !== "reviewed") {
        dispatch(
          updateVisitData(token, visitDetails?.data[0]?.patient_visit, {
            status: "reviewed",
          })
        );
      }
    }
  }, [dispatch, token, visitDetails?.data]);

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  // Handle graph updates during play
  useEffect(() => {
    if (isPlaying && eegGraphData?.data) {
      updateGraph(dataCount);
    }
  }, [dataCount, isPlaying]);

  const createGraph = (canvasRef, data, label, location, dataCount, zoom) => {
    if (!canvasRef) {
      return;
    }

    // Get the appropriate data source (filtered or original)
    const sourceData = isFilter ? filteredEegData : data;

    // Flatten the data for panning
    const { flatData, flatLabels } = flattenEEGData(
      sourceData?.[label] || [],
      sourceData?.length || []
    );

    // Store flattened data for panning reference
    const fullDataRef = { flatData, flatLabels };

    // Load all data initially - panning will just change the visible range
    const { startTime, endTime } = getTimeWindow(dataCount);

    // Ensure at least 1 second span for x-axis stability with integer bounds
    let xMin = Math.floor(startTime);
    let xMax = Math.ceil(endTime);
    if (xMax - xMin < 1) {
      xMax = xMin + 1;
    }

    logWindow(`createGraph:${label}`, { min: xMin, max: xMax });

    // Use all flattened data, not just the visible window
    const initialLabels = flatLabels;
    const initialData = flatData;

    const chart = new Chart(canvasRef, {
      type: "line",
      data: {
        labels: initialLabels,
        datasets: [
          {
            data: initialData,
            backgroundColor: "rgba(0, 0, 0, 0)",
            borderColor: "rgba(0, 0, 0, 1)",
            borderWidth: 0.4,
            pointRadius: 0,
          },
        ],
      },
      options: {
        scales: {
          x: {
            type: "linear",
            grid: {
              display: true,
              drawOnChartArea: true,
              drawTicks: true,
              color: "rgba(0, 0, 0, 0.1)",
              lineWidth: 1,
            },
            display: true,
            ticks: {
              display: label === "CH2_Voltage",
              stepSize: 1,
              autoSkip: false,
              align: "center",
              maxRotation: 0,
              minRotation: 0,
              padding: 0,
              font: {
                size: 13,
              },
              callback: function (value) {
                return Number.isInteger(value) ? value : "";
              },
            },
            title: {
              display: label === "CH2_Voltage",
              text: "Time (seconds)",
              padding: 0,
              font: {
                size: 14,
              },
            },
            min: xMin,
            max: xMax,
          },
          y: {
            grid: {
              display: false,
            },
            ticks: {
              display: false,
            },
            offset: false,
            alignToPixels: true,
            title: {
              display: true,
              text: [location],
              font: {
                size: 14,
              },
            },
            grace: "10%",
            min: zoom?.yMin,
            max: zoom?.yMax,
          },
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 0,
            hitRadius: 0,
          },
          line: {
            tension: 0,
          },
        },
        plugins: {
          tooltip: {
            enabled: false, // Disable tooltip for smoother pan and no hover data
          },
          legend: {
            display: false,
          },
          decimation: {
            enabled: true,
            algorithm: "min-max", // Best for line charts with large data
          },
          zoom: {
            pan: {
              enabled: true,
              mode: "y", // Only allow vertical dragging
              // Throttle pan updates for smoothness
              onPan: (() => {
                let rafId = null;
                let lastArgs = null;
                return function throttledOnPan(args) {
                  lastArgs = args;
                  if (!rafId) {
                    rafId = requestAnimationFrame(() => {
                      rafId = null;
                      try {
                        const { chart } = lastArgs;
                        if (!chart || !chart.scales || !chart.scales.x) return;
                        const xAxis = chart.scales.x;
                        let minX = xAxis.min;
                        let maxX = xAxis.max;
                        if (
                          !fullDataRef.flatLabels ||
                          fullDataRef.flatLabels.length === 0
                        )
                          return;
                        const totalDataDuration = Math.max(
                          ...fullDataRef.flatLabels
                        );
                        const windowSize = maxX - minX;
                        if (minX < 0) {
                          minX = 0;
                          maxX = windowSize;
                        }
                        if (maxX > totalDataDuration) {
                          maxX = totalDataDuration;
                          minX = totalDataDuration - windowSize;
                        }
                        updateBothChartsOnPan(chart, minX, maxX);
                      } catch (error) {}
                    });
                  }
                };
              })(),

              onPanComplete: ({ chart }) => {
                try {
                  if (chart && chart.scales && chart.scales.x) {
                    setCurrentPanWindow({
                      min: chart.scales.x.min,
                      max: chart.scales.x.max,
                    });

                    setDataCount(
                      Math.round(chart.scales.x.min / WINDOW_SIZE_SECONDS)
                    );
                  }
                } catch (error) {}
              },
            },
            zoom: {
              wheel: {
                enabled: false,
              },
              pinch: {
                enabled: false,
              },
              mode: "y",
            },
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        maintainAspectRatio: false,
        animation: false,
        spanGaps: true,
        layout: {
          padding: {
            right: 10,
          },
        },
      },
    });

    canvasRef.chart = chart;
    canvasRef.fullDataRef = fullDataRef; // Store reference for updates
    canvasRef.channelLabel = label; // Store channel label for tick preservation

    return chart;
  };

  const handleChartZoomIn = (index) => {
    const ref = chartRefs.current[index];
    if (ref?.chart) {
      const currentScale = ref.chart.scales.y;
      const range = currentScale.max - currentScale.min;
      const center = (currentScale.max + currentScale.min) / 2;
      const newRange = range * 0.5;
      const yMin = center - newRange / 2;
      const yMax = center + newRange / 2;
      ref.chart.options.scales.y.min = yMin;
      ref.chart.options.scales.y.max = yMax;
      ref.chart.update();
      setZoomState((prev) => {
        const updated = [...prev];
        updated[index] = { yMin, yMax };
        return updated;
      });
    }
  };

  const handleChartZoomOut = (index) => {
    const ref = chartRefs.current[index];
    if (ref?.chart) {
      const currentScale = ref.chart.scales.y;
      const range = currentScale.max - currentScale.min;
      const center = (currentScale.max + currentScale.min) / 2;
      const newRange = range * 2;
      const yMin = center - newRange / 2;
      const yMax = center + newRange / 2;
      ref.chart.options.scales.y.min = yMin;
      ref.chart.options.scales.y.max = yMax;
      ref.chart.update();
      setZoomState((prev) => {
        const updated = [...prev];
        updated[index] = { yMin, yMax };
        return updated;
      });
    }
  };

  const handleChartResetZoom = (index) => {
    const ref = chartRefs.current[index];
    if (ref?.chart) {
      ref.chart.options.scales.y.min = undefined;
      ref.chart.options.scales.y.max = undefined;
      ref.chart.update("none");
      setZoomState((prev) => {
        const updated = [...prev];
        updated[index] = { yMin: undefined, yMax: undefined };
        return updated;
      });
    }
  };

  useEffect(() => {
    const fetchVisit = async () => {
      try {
        const visits = await dispatch(getPatientVisitDetails(token, params.id));

        if (visits?.data?.length > 0) {
          const relatedIds = visits?.data?.map((record) => record.id);
          setRelatedIds(relatedIds);
        } else {
          toast.error("Visit details not found");
        }
      } catch (error) {
        setLoading(false);

        toast.error("Error fetching data of patient visit", {
          autoClose: 3000,
          position: "top-right",
        });
      }
    };
    setPatientData(null);
    fetchVisit();
  }, [dispatch, token, params.id]);

  useEffect(() => {
    const fetchEegData = async () => {
      try {
        if (relatedIds?.length > 0) {
          setLoading(true);
          await dispatch(getEegGraph(token, relatedIds[currentIndex]));
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);

        toast.error("Error fetching data", {
          autoClose: 3000,
          position: "top-right",
        });
      }
    };
    setEditing(false);
    // Reset filter state when switching to new patient
    setIsFilter(false);
    setFilteredEegData(null);
    setLffValue("Select");
    setHffValue("Select");
    setNotchValue("Select");
    // Reset all UI elements to start position
    setDataCount(0);
    setCurrentPanWindow({ min: 0, max: WINDOW_SIZE_SECONDS });
    setScrollbarPosition(WINDOW_SIZE_SECONDS);
    setZoomState([
      { yMin: undefined, yMax: undefined },
      { yMin: undefined, yMax: undefined },
    ]);
    fetchEegData();
  }, [dispatch, token, currentIndex, relatedIds]);

  // Fix the useEffect to calculate duration from loaded data
  useEffect(() => {
    if (!loading && eegGraphData) {
      setPatientData(eegGraphData?.data?.patient_data);
      const currentData = eegGraphData?.data;
      setDataLength(currentData.CH1_Voltage?.length || 0);
      setEegData(eegGraphData?.data);

      // Log concise summary for loaded EEG data
      logEEGDataSummary("loaded", currentData);

      // Compute duration from samples
      const totalSamples =
        currentData.CH1_Voltage?.reduce(
          (sum, chunk) => sum + chunk.length,
          0
        ) || 0;
      const originalDuration = totalSamples / 250;
      setTotalDataDuration(originalDuration);
      console.log(`[EEG] Total duration≈${originalDuration.toFixed(3)}s`);

      // Initialize slider position to first window end after data is loaded
      const initialWindowEnd = Math.min(WINDOW_SIZE_SECONDS, originalDuration);
      setTimeout(() => {
        setScrollbarPosition(initialWindowEnd);
        // Also initialize the pan window to match
        setCurrentPanWindow({ min: 0, max: initialWindowEnd });
      }, 100);

      const location = (
        visitDetails?.data?.[currentIndex]?.location || "N/A"
      ).split("-");

      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });

      const newCharts = ["CH1_Voltage", "CH2_Voltage"].map((channelName, i) =>
        createGraph(
          chartRefs.current[i],
          currentData,
          channelName,
          location[i],
          dataCount,
          zoomState[i]
        )
      );

      setCharts(newCharts);

      // Initialize pan window state - use actual duration if less than window size
      const { startTime, endTime } = getTimeWindow(dataCount);
      const actualEndTime = Math.min(endTime, originalDuration);
      setCurrentPanWindow({ min: startTime, max: actualEndTime });
      logWindow("initial", { min: startTime, max: actualEndTime });
    }
    return () => {
      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });
    };
  }, [loading, eegGraphData, isFilter]);

  const handleFilterChange = async (filterType, value) => {
    switch (filterType) {
      case "lff":
        setLffValue(value);
        break;
      case "hff":
        setHffValue(value);
        break;
      case "notch":
        setNotchValue(value);
        break;
      case "enhance":
        setLffValue("1Hz");
        setHffValue("70Hz");
        setNotchValue("60Hz");
      default:
        break;
    }

    if (filterType === "enhance") {
      await applyFiltersWithValues("1Hz", "70Hz", "60Hz");
    } else {
      const lff =
        filterType === "lff" ? value : lffValue === "Select" ? "N/A" : lffValue;
      const hff =
        filterType === "hff" ? value : hffValue === "Select" ? "N/A" : hffValue;
      const notch =
        filterType === "notch"
          ? value
          : notchValue === "Select"
          ? "N/A"
          : notchValue;

      if (lff !== "Select" || hff !== "Select" || notch !== "Select") {
        await applyFiltersWithValues(lff, hff, notch);
      }
    }
  };

  const applyFiltersWithValues = async (lff, hff, notch) => {
    setLoadingFilter(true);
    try {
      const filterData = await dispatch(
        getEegGraphFilter(token, [relatedIds[currentIndex]], lff, hff, notch)
      );

      if (filterData.length > 0) {
        setDataLength(filterData[0]?.CH1_Voltage?.length || 0);
        setIsFilter(true);
        setFilteredEegData(filterData[0]);
        setDataCount(0);
        // Log concise summary for filtered EEG data
        logEEGDataSummary(
          `filtered lff=${lff} hff=${hff} notch=${notch}`,
          filterData[0]
        );
      } else {
        toast.error("Error applying filters");
      }
    } catch (error) {
      toast.error("Error applying filters");
    } finally {
      setLoadingFilter(false);
    }
  };

  const resetFilter = () => {
    setDataCount(0);
    setIsFilter(false);
    setFilteredEegData(null);
    setLffValue("Select");
    setHffValue("Select");
    setNotchValue("Select");
    // Recalculate total duration after filter reset
    if (eegData) {
      const totalSamples =
        eegData.CH1_Voltage?.reduce((sum, chunk) => sum + chunk.length, 0) || 0;
      const originalDuration = totalSamples / 250;
      setTotalDataDuration(originalDuration);
      // Reset pan window state to beginning, handling short durations
      const initialWindowEnd = Math.min(WINDOW_SIZE_SECONDS, originalDuration);
      setCurrentPanWindow({ min: 0, max: initialWindowEnd });
    }
  };

  useEffect(() => {
    if (isFilter && filteredEegData && !loadingFilter) {
      // When filter is applied, recreate graphs with filtered data
      const location = (
        visitDetails?.data?.[currentIndex]?.location || "N/A"
      ).split("-");

      chartRefs.current.forEach((canvasRef) => {
        if (canvasRef?.chart) {
          canvasRef.chart.destroy();
          canvasRef.chart = null;
        }
      });

      const newCharts = ["CH1_Voltage", "CH2_Voltage"].map((channelName, i) =>
        createGraph(
          chartRefs.current[i],
          eegGraphData?.data, // Pass original data, createGraph will handle filtering
          channelName,
          location[i],
          0, // Reset to beginning when filter is applied
          zoomState[i]
        )
      );

      setCharts(newCharts);
      setDataCount(0); // Reset data count when filter is applied

      // Reset pan window state when filter is applied - handle short durations
      const totalDuration = getTotalDataDuration();
      const initialWindowEnd = Math.min(WINDOW_SIZE_SECONDS, totalDuration);
      setCurrentPanWindow({ min: 0, max: initialWindowEnd });
    }
  }, [isFilter, filteredEegData, loadingFilter]);

  const downloadCSV = async () => {
    setDownloading(true);
    try {
      const fileData = await dispatch(
        getCsvFile(
          token,
          visitDetails?.data[currentIndex]?.id,
          visitDetails?.data[currentIndex]?.eeg_file_s3
        )
      );
      if (fileData) {
        const fileUrl = fileData?.presigned_url;
        if (fileUrl) {
          const link = document.createElement("a");
          link.href = fileUrl;
          link.download = fileData?.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.error("Error: Failed to retrieve file URL");
      }
    } catch (error) {
      toast.error("Failed to download the file");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (token) {
      const data = token.split(".")[1];
      const parsedData = JSON.parse(atob(data));
      setIsFirmwareAdmin(parsedData.data[0].is_firmware_admin);
      setIsResearchAdmin(parsedData.data[0].is_research_admin);
    }
  }, []);

  return (
    <>
      <div className="recording2">
        <div className="recording-graph-panel">
          <div className="container-fluid">
            <div className="row info-container">
              {/* Personal Info */}
              <div className="col-md-4 col-12 order-1 mb-3 mb-md-0">
                <div className="personal-info">
                  <div className="btn-back">
                    <span className="back-info-btn">
                      <img
                        src={BackIcon}
                        alt="back-icon"
                        onClick={handleBack}
                        width={24}
                        height={24}
                      />
                    </span>
                    <div className="back-info-btn" onClick={handleBack}>
                      Back
                    </div>
                  </div>
                  <h1 className="tw-text-xl tw-uppercase">
                    {patientData?.category === "clinic"
                      ? patientData?.name ?? "N/A"
                      : patientData?.id_number ?? "N/A"}
                  </h1>
                  <div className="info-wrapper">
                    {patientData?.category === "clinic" && (
                      <div className="info">
                        <p>Patient ID: </p>
                        <p>{patientData?.id_number || "N/A"} </p>
                      </div>
                    )}

                    <div className="info">
                      <p>Age / Gender: </p>
                      <p>
                        {patientData?.age || "N/A"}{" "}
                        {genderMap[patientData?.gender] || "N/A"}
                      </p>
                    </div>
                    <div className="info">
                      <p>Recording Date: </p>
                      <p>
                        {patientData?.timestamp
                          ? formatDate(patientData?.timestamp)
                          : "N/A"}
                      </p>
                    </div>
                    <div className="info">
                      <p>Condition: </p>
                      <p>
                        {conditionMap[patientData?.clinical_condition] || "N/A"}
                      </p>
                    </div>
                    <div className="info">
                      <p>Location Tag: </p>
                      <div className="dropdown">
                        <button
                          className="btn green-btn2 btn-sm dropdown-toggle"
                          type="button"
                          id="dropdownMenuLocationTag"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          {selectedLocationTag ||
                            visitDetails?.data?.[currentIndex]?.location_tag ||
                            "N/A"}
                        </button>
                        <div
                          className="dropdown-menu"
                          aria-labelledby="dropdownMenuLocationTag"
                        >
                          {dummyLocationTags.map((tag) => (
                            <button
                              className="dropdown-item"
                              type="button"
                              key={tag}
                              onClick={() => {
                                setPendingLocationTag(tag);
                                setShowLocationTagConfirm(true);
                              }}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Buttons */}
              <div className="col-md-3 col-12 order-2 mb-3 mb-md-0 d-flex flex-column  justify-content-center ">
                <div className="btns-container">
                  <button
                    type="button"
                    className="button btn-invert-rec"
                    disabled={loading}
                    onClick={() => setEditing(true)}
                  >
                    EDIT NOTES
                  </button>
                  {(isResearchAdmin || isFirmwareAdmin) && (
                    <button
                      className="button btn-default-rec"
                      onClick={downloadCSV}
                      disabled={downloading || loading}
                    >
                      <span style={{ fontSize: "14px" }}>
                        {downloading ? "Downloading" : "Download CSV"}
                      </span>
                    </button>
                  )}
                </div>
              </div>
              {/* Notes Editor */}
              {visitDetails?.data[currentIndex] && (
                <div
                  className="col-md-5 col-12 order-3 d-flex justify-content-md-end justify-content-center align-items-center"
                  style={{ minHeight: 180 }}
                >
                  <div style={{ width: "100%", maxWidth: 400 }}>
                    <NotesEditor
                      currentIndex={currentIndex}
                      editing={editing}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="eeg-graph-panel">
            {loading || loadingFilter ? (
              <div className="linear-loader">
                <LinearWithValueLabel increment={1} />
              </div>
            ) : (
              <>
                <div className="eeg-header">
                  <div className="header-stats">
                    <div className="header-stat">
                      <p>
                        Display: <span>10s</span>
                      </p>
                    </div>
                    <div className="header-stat">
                      <p>
                        Scale: <span>10uv/mm</span>
                      </p>
                    </div>
                    <div className="header-stat">
                      <p>LFF: </p>
                      <div className="dropdown" style={{ paddingLeft: "4px" }}>
                        <button
                          className="btn green-btn2 btn-sm dropdown-toggle"
                          type="button"
                          id="dropdownMenuLFF"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          {lffValue}
                        </button>
                        <div
                          className="dropdown-menu"
                          aria-labelledby="dropdownMenuLFF"
                        >
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("lff", "N/A")}
                          >
                            N/A
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("lff", "1Hz")}
                          >
                            1Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("lff", "5Hz")}
                          >
                            5Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("lff", "15Hz")}
                          >
                            15Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("lff", "20Hz")}
                          >
                            20Hz
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="header-stat">
                      <p>HFF: </p>
                      <div className="dropdown" style={{ paddingLeft: "4px" }}>
                        <button
                          className="btn green-btn2 btn-sm dropdown-toggle"
                          type="button"
                          id="dropdownMenuHFF"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          {hffValue}
                        </button>
                        <div
                          className="dropdown-menu"
                          aria-labelledby="dropdownMenuHFF"
                        >
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("hff", "N/A")}
                          >
                            N/A
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("hff", "15Hz")}
                          >
                            15 Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("hff", "30Hz")}
                          >
                            30 Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("hff", "40Hz")}
                          >
                            40 Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("hff", "70Hz")}
                          >
                            70 Hz
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="header-stat">
                      <p>Notch: </p>
                      <div className="dropdown" style={{ paddingLeft: "4px" }}>
                        <button
                          className="btn btn-sm green-btn2 dropdown-toggle"
                          type="button"
                          id="dropdownMenuNotch"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          {notchValue}
                        </button>
                        <div
                          className="dropdown-menu"
                          aria-labelledby="dropdownMenuNotch"
                        >
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("notch", "N/A")}
                          >
                            N/A
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("notch", "50Hz")}
                          >
                            50 Hz
                          </button>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange("notch", "60Hz")}
                          >
                            60 Hz
                          </button>
                        </div>
                      </div>

                      <div className="control-buttons">
                        <Tooltip title="Reset filter">
                          <button type="button" onClick={() => resetFilter()}>
                            <img src={resetFilterIcon} alt="reset filter" />
                          </button>
                        </Tooltip>

                        <Tooltip title="Enhance (LFF=1Hz, HFF=70Hz, Notch=60Hz)">
                          <button
                            type="button"
                            onClick={() =>
                              handleFilterChange(
                                "enhance",
                                "(LFF=1Hz, HFF=70Hz, Notch=60Hz)"
                              )
                            }
                          >
                            <img src={enhanceIcon} alt="enhance" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                  <div className="location-btns">
                    {visitDetails?.data?.length > 1 && (
                      <button
                        className="btn loc-btn"
                        type="button"
                        onClick={() => {
                          if (currentIndex > 0) {
                            setCurrentIndex((prevIndex) =>
                              prevIndex > 0 ? prevIndex - 1 : 0
                            );
                            resetFilter();
                          }
                        }}
                        disabled={currentIndex === 0}
                      >
                        <ArrowBackIos
                          style={{ fontSize: 12, marginRight: 4 }}
                        />
                        <span className="loc-name">Back</span>
                      </button>
                    )}

                    <span className="loc-name">
                      {visitDetails?.data?.[currentIndex]?.location ?? "N/A"}
                    </span>
                    {visitDetails?.data?.length > 1 && (
                      <button
                        className="btn loc-btn"
                        type="button"
                        onClick={() => {
                          if (
                            currentIndex < relatedIds.length - 1 ||
                            currentIndex <= 4
                          ) {
                            setCurrentIndex((prevIndex) => prevIndex + 1);
                            resetFilter();
                          }
                        }}
                        disabled={
                          currentIndex === relatedIds.length - 1 ||
                          currentIndex > 4
                        }
                      >
                        <span className="loc-name">Next</span>
                        <ArrowForwardIos
                          style={{ fontSize: 12, marginLeft: 4 }}
                        />
                      </button>
                    )}
                  </div>
                </div>

                <div className="graph-box mt-2">
                  {[1, 2].map((_, index) => (
                    <>
                      <div
                        key={index}
                        className="canvas-wrapper"
                        style={{ position: "relative" }}
                      >
                        <canvas
                          ref={(el) => (chartRefs.current[index] = el)}
                          height={index === 1 ? 140 : 110}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 2,
                            right: -23,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <button
                            className="control-button"
                            onClick={() => handleChartZoomIn(index)}
                            title="Zoom In"
                          >
                            <img
                              src={zoomInIcon}
                              alt="zoom in"
                              style={{ width: 18, height: 18 }}
                            />
                          </button>
                          <button
                            className="control-button"
                            onClick={() => handleChartZoomOut(index)}
                            title="Zoom Out"
                          >
                            <img
                              src={zoomOutIcon}
                              alt="zoom out"
                              style={{ width: 18, height: 18 }}
                            />
                          </button>
                          <button
                            className="control-button"
                            onClick={() => handleChartResetZoom(index)}
                            title="Reset Zoom"
                          >
                            <img
                              src={resetZoomIcon}
                              alt="reset zoom"
                              style={{ width: 18, height: 18 }}
                            />
                          </button>
                        </div>
                      </div>
                    </>
                  ))}
                  {/* Horizontal Scrollbar */}
                  <div
                    className="scrollbar-container"
                    style={{
                      marginTop: "10px",
                      padding: "0 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "#666" }}>
                      {Math.round(currentPanWindow.min)}s/
                      {Math.round(scrollbarPosition)}s
                    </span>
                    <input
                      type="range"
                      min={Math.min(
                        WINDOW_SIZE_SECONDS,
                        getTotalDataDuration()
                      )}
                      max={getTotalDataDuration()}
                      step="1"
                      value={scrollbarPosition}
                      onChange={handleScrollbarChange}
                      style={{
                        flex: 1,
                        height: "6px",
                        borderRadius: "3px",
                        background: "#ddd",
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontSize: "12px", color: "#666" }}>
                      {getTotalDataDuration()}s
                    </span>
                  </div>
                </div>

                <div className="eeg-graph-controls">
                  <button
                    disabled={!canNavigateBackward()}
                    className="control-button"
                    onClick={() => handleGraphButton("fast-backward")}
                  >
                    <img src={fastBackwardIcon} alt="fast backward" />
                  </button>

                  <button
                    disabled={!canNavigateBackward()}
                    className="control-button"
                    onClick={() => handleGraphButton("backward")}
                  >
                    <img src={backwardIcon} alt="backward" />
                  </button>

                  <button
                    disabled={!canNavigateBackward()}
                    className="control-button"
                    onClick={() => handleGraphButton("five-backward")}
                  >
                    <img src={fiveSecondBackward} alt="5s backward" />
                  </button>

                  <button
                    className="control-button"
                    onClick={() =>
                      handleGraphButton(isPlaying ? "pause" : "play")
                    }
                  >
                    <img
                      src={isPlaying ? pauseIcon : playIcon}
                      alt={isPlaying ? "pause" : "play"}
                    />
                  </button>

                  <button
                    disabled={!canNavigateForward()}
                    className="control-button"
                    onClick={() => handleGraphButton("five-forward")}
                  >
                    <img src={fiveSecondForward} alt="5s forward" />
                  </button>

                  <button
                    disabled={!canNavigateForward()}
                    className="control-button"
                    onClick={() => handleGraphButton("forward")}
                  >
                    <img src={forwardIcon} alt="forward" />
                  </button>

                  <button
                    disabled={!canNavigateForward()}
                    className="control-button"
                    onClick={() => handleGraphButton("fast-forward")}
                  >
                    <img src={fastForwardIcon} alt="fast forward" />
                  </button>
                </div>
              </>
            )}
          </div>
          <div
            className={loading || loadingFilter ? "pwelch-hide" : "pwelch-show"}
          >
            {relatedIds && eegGraphData?.data && !loading && !loadingFilter && (
              <HeatMap
                id={relatedIds[currentIndex]}
                currentTimeWindow={currentPanWindow}
              />
            )}
          </div>
        </div>
      </div>
      <UpdateLocationTagConfirmModal
        show={showLocationTagConfirm}
        handleClose={() => setShowLocationTagConfirm(false)}
        token={token}
        visitDetailId={visitDetails?.data?.[currentIndex]?.id}
        parentVisitId={params.id}
        selectedTag={pendingLocationTag}
        onUpdated={() => {
          setSelectedLocationTag(pendingLocationTag);
          setPendingLocationTag("");
        }}
      />
    </>
  );
};

export default Recording;
